import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";

interface IProps {
  isModalOpen: boolean;
  onClose?: () => void;
  cancelButtonRef?: React.MutableRefObject<HTMLElement | null>;
  children: React.ReactNode;
}

/**
 * Abstraction to create modal dialogs.
 *
 * By default, clicking the backdrop or pressing Escape will close the modal
 * only if an onClose handler is provided.
 */
const ModalFrame = ({
  isModalOpen,
  onClose,
  cancelButtonRef,
  children,
}: IProps) => {
  const handleClose = () => {
    if (onClose) onClose();
  };

  return (
    <Transition.Root show={isModalOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[200]"
        initialFocus={cancelButtonRef}
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[201] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className="relative w-full max-w-lg transform overflow-hidden rounded-2xl
                           bg-white px-4 pt-5 pb-4 text-left shadow-2xl transition-all
                           sm:p-6"
              >
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ModalFrame;
